import { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { Classroom, CubeQuery } from "@models/ISchema";
import { classroomQuery } from "../graphql/classroom.query";
import { useDebounce } from "@common/hooks/useDebounce";

export interface ClassroomOption extends Classroom {
  label: string;
  value: string;
  isDisabled: boolean;
}

const serializeOptions = (classrooms: Classroom[]): ClassroomOption[] => {
  if (!classrooms?.length) return [];

  return classrooms.map(classroom => ({
    ...classroom,
    label: `${classroom.code} - ${classroom.name} - ${classroom?.building?.campus?.code || ""}`,
    value: classroom.id,
    isDisabled: !classroom?.usable,
  }));
};

export const useClassroomSearch = () => {
  const client = useApolloClient();
  const { scenario, origin }: IParams = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ClassroomOption[]>([]);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const fetchOptions = async (value: string) => {
    try {
      setLoading(true);
      if (value !== "") {
        const { data } = await client.query({
          query: classroomQuery,
          variables: {
            originId: origin,
            scenarioId: scenario,
            filter: {
              page: 1,
              size: 20,
              searchTerm: value,
            },
          },
        });
        const dataQuery: CubeQuery = data?.cube;
        setOptions(serializeOptions(dataQuery?.classrooms?.items));
      }
      setLoading(false);
    } catch {
      setOptions([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedSearch) {
      setLoading(true);
      fetchOptions(debouncedSearch);
    } else {
      setOptions([]);
    }
  }, [debouncedSearch]);

  return {
    options,
    loading,
    onSearch: setSearchTerm,
  };
};

export default useClassroomSearch;
